"use client";
import React, { useState, useEffect } from "react";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { InputPrimary } from "@/components/common/InputPrimary";
import { TextboxPrimary } from "@/components/common/textBoxPrimary";
import { AnimatedSidebarPanel } from "../../layout/AnimatedSidebarPanel";
import { ImageUpload } from "../../ui/elements/ImageUpload";
import { SeoService } from "@/services/seoService";
import { generateImageSrc } from "@/utils/helper";

interface SeoSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  pageId?: string; // Add pageId prop
  currentPage?: any; // Current page object for fallback data
}

export function SeoSettingsPanel({
  isOpen,
  onClose,
  pageId = "",
  currentPage,
}: SeoSettingsPanelProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seoData, setSeoData] = useState({
    title: "",
    slug: "",
    description: "",
    featuredImageAssetId: "",
    featuredImageUrl: "",
  });

  // Get current page from Redux store to get store_id

  // Load SEO data when panel opens
  useEffect(() => {
    const loadSeoData = async () => {
      if (!pageId) {
        console.warn("⚠️ SEO Panel: Missing pageId for SEO data loading");
        return;
      }

      console.log("🔍 SEO Panel: Loading SEO data for page:", pageId);
      console.log("📄 SEO Panel: Current page data:", currentPage);
      setLoading(true);

      try {
        // First, try to load from API
        const seoInfo = await SeoService.getPageSeo(pageId);
        console.log("📥 SEO Panel: Received SEO data from API:", seoInfo);

        if (seoInfo) {
          // Generate CDN URL if asset exists
          let imageUrl = "";
          if (seoInfo.featured_image_asset?.path) {
            imageUrl = generateImageSrc(seoInfo.featured_image_asset.path);
            console.log(
              "🖼️ SEO Panel: Generated image URL from API:",
              imageUrl,
            );
          }

          const newData = {
            title: seoInfo.title || "",
            slug: seoInfo.slug || "",
            description: seoInfo.description || "",
            featuredImageAssetId: seoInfo.featured_image_asset_id || "",
            featuredImageUrl: imageUrl,
          };
          console.log("✅ SEO Panel: Setting SEO data from API:", newData);
          setSeoData(newData);
        } else if (currentPage) {
          // Fallback to currentPage data if API returns null
          console.log("🔄 SEO Panel: Using fallback data from currentPage");
          let imageUrl = "";
          if (currentPage.featured_image_asset?.path) {
            imageUrl = generateImageSrc(currentPage.featured_image_asset.path);
            console.log(
              "🖼️ SEO Panel: Generated image URL from currentPage:",
              imageUrl,
            );
          }

          const fallbackData = {
            title: currentPage.title || "",
            slug: currentPage.slug || "",
            description: currentPage.description || "",
            featuredImageAssetId: currentPage.featured_image_asset_id || "",
            featuredImageUrl: imageUrl,
          };
          console.log(
            "✅ SEO Panel: Setting SEO data from fallback:",
            fallbackData,
          );
          setSeoData(fallbackData);
        } else {
          console.warn("⚠️ SEO Panel: No SEO data found, using empty state");
          // Reset to empty state when no data exists
          setSeoData({
            title: "",
            slug: "",
            description: "",
            featuredImageAssetId: "",
            featuredImageUrl: "",
          });
        }
      } catch (error) {
        console.error("❌ SEO Panel: Failed to load SEO data from API:", error);

        // Try fallback to currentPage on error
        if (currentPage) {
          console.log(
            "🔄 SEO Panel: Using fallback data from currentPage after error",
          );
          let imageUrl = "";
          if (currentPage.featured_image_asset?.path) {
            imageUrl = generateImageSrc(currentPage.featured_image_asset.path);
            console.log(
              "🖼️ SEO Panel: Generated image URL from currentPage:",
              imageUrl,
            );
          }

          const fallbackData = {
            title: currentPage.title || "",
            slug: currentPage.slug || "",
            description: currentPage.description || "",
            featuredImageAssetId: currentPage.featured_image_asset_id || "",
            featuredImageUrl: imageUrl,
          };
          console.log(
            "✅ SEO Panel: Setting SEO data from fallback after error:",
            fallbackData,
          );
          setSeoData(fallbackData);
        } else {
          // Reset to empty state on error with no fallback
          setSeoData({
            title: "",
            slug: "",
            description: "",
            featuredImageAssetId: "",
            featuredImageUrl: "",
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && pageId) {
      loadSeoData();
    }
  }, [isOpen, pageId, currentPage]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 60) {
      setSeoData((prev) => ({ ...prev, title: value }));
    }
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    if (value.length <= 160) {
      setSeoData((prev) => ({ ...prev, description: value }));
    }
  };

  const handleImageSelected = (
    pageId: string,
    selectedFile: { assetId: string; url: string },
  ) => {
    console.log("🖼️ SEO image selected:", selectedFile);
    setSeoData((prev) => ({
      ...prev,
      featuredImageAssetId: selectedFile.assetId,
      featuredImageUrl: selectedFile.url,
    }));
  };

  const handleRemoveImage = (_pageId: string) => {
    setSeoData((prev) => ({
      ...prev,
      featuredImageAssetId: "",
      featuredImageUrl: "",
    }));
  };

  const handleSave = async () => {
    if (!pageId) {
      console.error("Missing pageId or storeId for SEO save");
      return;
    }

    setSaving(true);
    try {
      await SeoService.updatePageSeo(pageId, {
        title: seoData.title,
        slug: seoData.slug,
        description: seoData.description,
        featured_image_asset_id: seoData.featuredImageAssetId || undefined,
      });

      onClose();
    } catch (error) {
      console.error("Failed to save SEO settings:", error);
      // TODO: Show error message to user
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatedSidebarPanel
      isOpen={isOpen}
      onClose={onClose}
      title="Page SEO Settings"
      subtitle="Configure meta information for search engines"
    >
      <div className="p-4 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="ml-2 text-sm text-muted-foreground">
              Loading SEO data...
            </span>
          </div>
        ) : (
          <>
            {/* Title Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Page Title</label>
              <div className="space-y-1">
                <InputPrimary
                  value={seoData.title}
                  onChange={handleTitleChange}
                  placeholder="Enter page title..."
                  className="w-full"
                />
                <div className="text-xs text-muted-foreground flex justify-between">
                  <span>Recommended: 50-60 characters</span>
                  <span
                    className={
                      seoData.title.length > 50
                        ? "text-yellow-500"
                        : seoData.title.length > 60
                          ? "text-red-500"
                          : ""
                    }
                  >
                    {seoData.title.length}/60
                  </span>
                </div>
              </div>
            </div>

            {/* Slug Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Page Slug</label>
              <div className="space-y-1">
                <InputPrimary
                  value={seoData.slug}
                  onChange={(e) =>
                    setSeoData((prev) => ({ ...prev, slug: e.target.value }))
                  }
                  placeholder="page-slug"
                  className="w-full"
                />
                <div className="text-xs text-muted-foreground">
                  The URL path for this page (e.g., /page-slug)
                </div>
              </div>
            </div>

            {/* Description Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Meta Description</label>
              <div className="space-y-1">
                <TextboxPrimary
                  value={seoData.description}
                  onChange={handleDescriptionChange}
                  placeholder="Enter meta description..."
                  className="w-full h-20 resize-none"
                />
                <div className="text-xs text-muted-foreground flex justify-between">
                  <span>Recommended: 150-160 characters</span>
                  <span
                    className={
                      seoData.description.length > 150
                        ? "text-yellow-500"
                        : seoData.description.length > 160
                          ? "text-red-500"
                          : ""
                    }
                  >
                    {seoData.description.length}/160
                  </span>
                </div>
              </div>
            </div>

            {/* Featured Image */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Featured Image</label>
              <p className="text-xs text-muted-foreground">
                This image will be displayed when your page is shared on social
                media
              </p>

              <ImageUpload
                pageId={"seo-panel"}
                currentImage={seoData.featuredImageUrl}
                isUploading={false}
                onImageSelected={handleImageSelected}
                onRemoveImage={handleRemoveImage}
              />
            </div>

            {/* Save Button */}
            <div className="pt-4 border-t border-border dark:border-auth-background">
              <ButtonPrimary
                onClick={handleSave}
                className="w-full rounded-full"
                disabled={saving || loading}
              >
                {saving ? "Saving..." : "Save SEO Settings"}
              </ButtonPrimary>
            </div>
          </>
        )}
      </div>
    </AnimatedSidebarPanel>
  );
}
